import { useCallback, useEffect, useState } from 'react';
import { ref, set, remove, onValue, push } from 'firebase/database';
import { database } from '../firebase';
import { toast } from '../utils/toast';

// Custom cases share the facilitator's private `archives/$identityKey` node
// (discriminated by `kind`) so no new top-level path, and therefore no
// security-rules change, is required.
export const CASE_KIND = 'case';

const genKey = () => push(ref(database, '_')).key;

export default function useCaseLibrary(identityKey, active) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!active || !identityKey) return undefined;
    const unsubscribe = onValue(
      ref(database, `archives/${identityKey}`),
      (snap) => {
        const list = Object.entries(snap.val() || {})
          .filter(([, v]) => v?.kind === CASE_KIND)
          .map(([id, v]) => ({ id, savedAt: v.savedAt, ...v.case }))
          .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
        setCases(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsubscribe;
  }, [identityKey, active]);

  // Saves under the given id, or mints a new one. Returns the id.
  const saveCase = useCallback(
    (caseDef, existingId) => {
      const id = existingId || genKey();
      const { id: _drop, savedAt: _drop2, ...clean } = caseDef;
      return set(ref(database, `archives/${identityKey}/${id}`), {
        kind: CASE_KIND,
        savedAt: Date.now(),
        case: { ...clean, id },
      })
        .then(() => id)
        .catch((error) => {
          console.error('Saving case failed:', error);
          toast('Could not save the case, check your connection.');
          throw error;
        });
    },
    [identityKey]
  );

  const deleteCase = useCallback(
    (id) => remove(ref(database, `archives/${identityKey}/${id}`))
      .catch(() => toast('Could not delete the case.')),
    [identityKey]
  );

  return { cases, loading, saveCase, deleteCase };
}
